<template>
  <div class="section-slot-page">
    <div class="back-bg"></div>
    <div class="bg-blur">
      <div class="conic-gradient"></div>
    </div>

    <transition name="route" mode="out-in">
      <PreLoader v-if="isLoadingCardGames" />
    </transition>

    <div class="container">
      <div class="section__header text-center">
        <h1 class="section__title">SLOTS</h1>
      </div>

      <!-- Game Type Filters -->
      <div class="d-flex flex-column w-100 position-sticky sticky-filters">
        <div class="game-filters">
          <button
            v-for="filter in gameFilters"
            :key="filter.type"
            class="filter-button"
            :class="{ active: selectedFilter === filter.type }"
            @click="setFilter(filter.type)"
          >
            {{ filter.label }}
          </button>
        </div>

        <!-- Search bar -->
        <div class="search-bar-container">
          <div class="search-bar" ref="searchBarContainer">
            <input
              title="Search games..."
              id="search-games-input"
              type="search"
              v-model="searchQuery"
              placeholder="Search games..."
              @input="filterGames"
              :class="{ expanded: isSearchExpanded }"
            />
            <button
              v-if="!isSearchExpanded"
              class="search-button fade-in-fwd"
              @click="toggleSearch"
              title="Search Games"
            >
              <font-awesome-icon
                icon="search"
                class="mx-1"
                aria-hidden="true"
              />
            </button>
            <button
              title="Close search box"
              @click="closeSearch"
              v-if="isSearchExpanded"
              class="cancel-search fade-in"
            >
              <font-awesome-icon
                icon="circle-xmark"
                class="mx-1"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      <!-- Game list -->
      <div
        class="slot-list-wrapper d-flex justify-content-center align-items-start w-100"
      >
        <div class="slot-list row">
          <!-- No results message -->
          <div
            v-if="noResults"
            class="no-results no-games-criteria text-center col-12 fade-in"
          >
            <p class="p">
              No {{ getSelectedFilterLabel.toLowerCase() }} games to show.
            </p>
          </div>

          <!-- Game cards -->
          <div
            v-else
            v-show="!isLoadingCardGames"
            class="game-card col-lg-4 col-md-4 col-sm-6 col-12"
            v-for="(game, index) in displayedGames"
            :key="game.id"
            :title="game.name"
            @click="launchGames(game.game_id, game.coming_soon)"
          >
            <div class="fade-in-fwd wrapper-coming-soon">
              <div
                v-if="game.coming_soon === 1"
                class="slot-game-comingsoon-label"
              >
                <p class="comingsoon-text mb-0">Coming soon</p>
              </div>
            </div>

            <img
              :id="`game-${index}`"
              :style="{ animationDelay: `${index * 0.09}s` }"
              class="slot-game-thumb fade-in-fwd"
              :src="`${getGameThumbnail(game.game_id)}?t=${version}`"
              @load="setImageLoaded(game, index)"
              @error="setFallbackImage($event)"
              :alt="game.name"
              :class="
                game.coming_soon === 0 ? 'cursor-pointer' : 'cursor-inherit'
              "
            />

            <p class="slot-game-name text-center">
              {{ game.name }}
            </p>

            <button
              v-if="isWindowWideEnough && game.coming_soon === 0"
              class="play-button"
              :title="`Play ${game.name}!`"
            >
              Play Now
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PreLoader from "@/components/Global/PreLoader.vue";
import pkg from "../../package.json"; // o il percorso corretto al tuo package.json

export default {
  name: "SlotPage",
  components: {
    PreLoader,
  },
  data() {
    return {
      searchQuery: "",
      version: pkg.version,
      publicPath: process.env.VUE_APP_PUBLIC_PATH ?? "/",
      lang: localStorage.getItem("language") ?? "en",
      token: "demo",
      currency: sessionStorage.getItem("currency") ?? "EUR",
      isWindowWideEnough: window.innerWidth > 600,
      fallbackImage: "/assets/images/logo.png",
      isSearchExpanded: false,
      selectedFilter: "all",
      gameFilters: [
        { type: "all", label: "All" },
        { type: "slot-game", label: "Slot" },
        { type: "crash-game", label: "Crash" },
        { type: "probability", label: "Instant Games" },
        { type: "coming-soon", label: "Coming Soon" },
      ],
    };
  },
  computed: {
    ...mapGetters("casinoGames", ["games", "isLoadingCardGames"]),

    noResults() {
      return !this.isLoadingCardGames && this.displayedGames.length === 0;
    },

    filteredGames() {
      if (!this.games?.games) return [];

      const query = this.searchQuery.toLowerCase();
      let filtered = this.games.games;

      if (query.length >= 3) {
        filtered = filtered.filter((game) =>
          game.name.toLowerCase().includes(query)
        );
      }

      filtered = filtered.filter((game) => {
        switch (this.selectedFilter) {
          case "slot-game":
            return game.type === "slot-game";
          case "crash-game":
            return game.type === "crash-game";
          case "probability":
            return game.type === "probability";
          case "coming-soon":
            return game.coming_soon === 1;
          default:
            return true;
        }
      });

      return filtered.reverse().sort((a, b) => {
        if (a.coming_soon === 1 && b.coming_soon !== 1) return -1;
        if (a.coming_soon !== 1 && b.coming_soon === 1) return 1;
        return 0;
      });
    },

    displayedGames() {
      return this.filteredGames;
    },

    getSelectedFilterLabel() {
      const selected = this.gameFilters.find(
        (filter) => filter.type === this.selectedFilter
      );
      return selected?.label ?? "Games";
    },
  },
  methods: {
    handlePopState() {
      this.$nextTick(() => {
        window.dispatchEvent(new Event("scroll"));
      });
    },
    addListeners() {
      window.addEventListener("resize", this.updateWindowWidth);
      window.addEventListener("scroll", this.handleScroll);
      document.addEventListener("click", this.handleOutsideClick);
    },
    removeListeners() {
      window.removeEventListener("resize", this.updateWindowWidth);
      window.removeEventListener("scroll", this.handleScroll);
      document.removeEventListener("click", this.handleOutsideClick);
    },
    setFilter(filterType) {
      this.selectedFilter = filterType;
      this.scrollToTop();
    },
    handleScroll() {
      const stickyEl = document.querySelector(".sticky-filters");
      if (!stickyEl) return;

      const scrollTop = window.scrollY || window.pageYOffset;
      const width = window.innerWidth;

      if (width <= 991) {
        // Per dispositivi mobili o piccoli: aggiungi sempre
        stickyEl.classList.add("sticky-filters-bg");
      } else {
        // Per dispositivi più grandi: gestisci con soglia scroll
        const threshold = 100;
        if (scrollTop >= threshold) {
          stickyEl.classList.add("sticky-filters-bg");
        } else {
          stickyEl.classList.remove("sticky-filters-bg");
        }
      }
    },
    toggleSearch(event) {
      event.stopPropagation();
      this.isSearchExpanded = true;

      this.$nextTick(() => {
        document.getElementById("search-games-input")?.focus();
      });

      document.addEventListener("click", this.handleOutsideClick);
    },
    closeSearch() {
      this.isSearchExpanded = false;
      this.searchQuery = "";
      document.removeEventListener("click", this.handleOutsideClick);
    },
    handleOutsideClick(event) {
      const searchBar = this.$refs.searchBarContainer;
      if (
        searchBar &&
        !searchBar.contains(event.target) &&
        this.isSearchExpanded &&
        this.searchQuery.length === 0
      ) {
        this.closeSearch();
      }
    },
    filterGames() {
      if (this.searchQuery.length >= 3) {
        this.displayedGames;
        this.scrollToTop();
      }
    },
    play(id, token) {
      this.$router.push(`/gameDetail/${id}/${token}`);
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    launchGames(game_id, comingSoon) {
      let token = this.token;
      if (comingSoon === 0) {
        this.play(game_id, token);
      }
    },
    getGameThumbnail(game_id) {
      return `https://storage.googleapis.com/camasy-images/casino/theearcasino/${game_id}.png`;
    },
    setFallbackImage(event) {
      event.target.src = this.fallbackImage;
    },
    setImageLoaded(game, index) {
      this.games.games[index].isImageLoaded = true;
    },
    updateWindowWidth() {
      this.isWindowWideEnough = window.innerWidth > 600;
    },
    setMetaTags() {
      const title = "The Ear | Slot";
      const metaDescription =
        "Discover the Slot Universe - Explore Our Exciting Slot Games Collection!";

      document.title = title;

      const metaDescriptionTag = document.querySelector(
        'meta[name="description"]'
      );
      if (metaDescriptionTag) {
        metaDescriptionTag.setAttribute("content", metaDescription);
      } else {
        const newMetaTag = document.createElement("meta");
        newMetaTag.name = "description";
        newMetaTag.content = metaDescription;
        document.head.appendChild(newMetaTag);
      }
    },
  },
  mounted() {
    this.addListeners();
    this.handleScroll();

    // Quando l'utente naviga con back/forward
    window.addEventListener("popstate", (event) => {
      console.log("Navigazione indietro/avanti intercettata");
      console.log("State:", event.state);
      setTimeout(() => {
        this.handlePopState();
      }, 250);
    });
  },
  activated() {
    this.addListeners();
    this.handleScroll();
  },
  deactivated() {
    this.removeListeners();
  },
  beforeUnmount() {
    this.removeListeners();
    window.removeEventListener("popstate", this.handlePopState);
  },
  watch: {
    $route() {
      this.$nextTick(() => {
        window.dispatchEvent(new Event("scroll"));
      });
    },
  },

  created() {
    let lang = localStorage.getItem("language") ?? "en";
    this.$router.push({ params: { lang: lang } });

    this.$store.dispatch("casinoGames/callEarGames");
    this.setMetaTags();
  },
};
</script>

<style scoped>
.game-filters {
  /* display: flex;
  justify-content: center;
  gap: 1rem;
  position: relative;
  margin-bottom: 10px;
  z-index: 2; */
  display: grid;
  justify-content: center;
  gap: 1rem;
  position: relative;
  margin-bottom: 10px;
  z-index: 2;
  margin: 0 auto 10px;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
}

.filter-button {
  padding: 0.5rem 1rem;
  background: transparent;
  color: white;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: clamp(14px, 1vw, 17px);
  letter-spacing: -0.09em;
  min-height: 35px;
  transition: 0.3s all ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.filter-button:hover {
  /* background: linear-gradient(to right, rgb(96, 115, 236), rgb(19, 54, 193)); */
  background: rgb(19, 54, 193);
  color: white;
  transition: 0.3s all ease;
}

.filter-button.active {
  background: rgb(19, 54, 193);
  color: white;
}

@media (max-width: 768px) {
  .game-filters {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .filter-button {
    font-size: 12px;
    padding: 0.4rem 0.8rem;
    line-height: 0.9;
  }
}

.min-w-100 {
  min-width: 100%;
}
</style>
